import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Sesión caducada: el token dura 2 h (backend/app/core/security.py). Al vencer, el servidor
// responde 401 "Token expirado" y el panel quedaba vacío, como si Talku estuviera caído.
// Se detecta por la fecha de vencimiento del token y por cualquier 401 de sesión.

const REDIRECT_SECONDS = 15;

const tokenExp = () => {
  try {
    const t = localStorage.getItem('talku_token') || '';
    const part = t.split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return json.exp ? json.exp * 1000 : null;
  } catch { return null; }
};

const hadAuth = (input, init) => {
  try {
    const h = (init && init.headers) || (input && input.headers);
    if (!h) return false;
    if (typeof h.get === 'function') return !!h.get('Authorization');
    return !!(h.Authorization || h.authorization);
  } catch { return false; }
};

export const goLogin = () => {
  try { localStorage.removeItem('talku_user'); localStorage.removeItem('talku_token'); } catch {}
  const here = window.location.pathname + window.location.search;
  window.location.replace('/login?next=' + encodeURIComponent(here));
};

// Símbolo de Talku (punto verde + dos arcos) en SVG para poder animarlo
function AnimatedSymbol() {
  return (
    <div className="se-logo">
      <span className="se-ripple"/><span className="se-ripple se-ripple--2"/>
      <svg viewBox="0 0 256 256" width="76" height="76" aria-hidden="true">
        <g className="se-face">
          <path className="se-smile" d="M38 128 A90 90 0 0 0 201.7 179.6" pathLength="1"/>
          <g className="se-brow-g"><path className="se-brow" d="M158.8 43.4 A90 90 0 0 1 198.9 72.6" pathLength="1"/></g>
          <circle className="se-dot" cx="83" cy="55" r="31"/>
        </g>
      </svg>
    </div>
  );
}

export default function SessionGuard() {
  const [expired, setExpired] = useState(false);
  const [left, setLeft] = useState(REDIRECT_SECONDS);
  const firedRef = useRef(false);

  useEffect(() => {
    const expire = () => { if (!firedRef.current) { firedRef.current = true; setExpired(true); } };

    // 1) Cualquier respuesta 401 de sesión (token vencido o inválido) en peticiones con sesión
    const orig = window.fetch;
    const wrapped = async (input, init) => {
      const r = await orig(input, init);
      if (r.status === 401 && hadAuth(input, init)) {
        r.clone().json().then(d => { if (/token/i.test(String(d && d.detail))) expire(); }).catch(() => {});
      }
      return r;
    };
    window.fetch = wrapped;

    // 2) Por la hora de vencimiento del token (aunque la pantalla esté quieta)
    const check = () => { const exp = tokenExp(); if (exp && Date.now() >= exp) expire(); };
    check();
    const iv = setInterval(check, 30000);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);

    return () => {
      if (window.fetch === wrapped) window.fetch = orig;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', check);
    };
  }, []);

  useEffect(() => {
    if (!expired) return;
    const iv = setInterval(() => setLeft(s => {
      if (s <= 1) { clearInterval(iv); goLogin(); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(iv);
  }, [expired]);

  if (!expired) return null;

  return createPortal(
    <div className="se-ov" role="dialog" aria-modal="true" aria-labelledby="se-t">
      <style>{`
        .se-ov{position:fixed;inset:0;z-index:100000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(17,17,17,.38);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);animation:se-fade .3s ease both;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .se-card{width:min(380px,100%);background:#fff;border:1px solid #EFEFEF;border-radius:10px;box-shadow:0 24px 60px rgba(0,0,0,.14),0 2px 6px rgba(0,0,0,.05);padding:30px 26px 22px;text-align:center;animation:se-up .45s cubic-bezier(.16,1,.3,1) .05s both;}
        .se-logo{position:relative;width:112px;height:112px;margin:0 auto 18px;display:flex;align-items:center;justify-content:center;}
        .se-logo svg{position:relative;z-index:1;overflow:visible;}
        .se-ripple{position:absolute;inset:14px;border-radius:50%;border:1px solid #E5E7EB;animation:se-ripple 2.8s ease-out 1.4s infinite;opacity:0;}
        .se-ripple--2{animation-delay:2.8s;}
        .se-smile,.se-brow{fill:none;stroke:#111;stroke-width:44;stroke-dasharray:1;stroke-dashoffset:1;}
        .se-smile{animation:se-draw .75s cubic-bezier(.65,0,.35,1) .25s forwards;}
        .se-brow{animation:se-draw .35s cubic-bezier(.65,0,.35,1) .9s forwards;}
        .se-brow-g{transform-origin:128px 128px;transform-box:view-box;animation:se-wink 3.2s ease-in-out 1.6s infinite;}
        .se-dot{fill:#88DA21;transform-origin:83px 55px;transform-box:view-box;transform:scale(0);animation:se-pop .5s cubic-bezier(.34,1.56,.64,1) 1.15s forwards,se-bob 3.2s ease-in-out 1.9s infinite;}
        .se-face{transform-origin:128px 128px;transform-box:view-box;animation:se-tilt 3.2s ease-in-out 1.6s infinite;}
        .se-t{font-size:18px;font-weight:500;color:#111;letter-spacing:-.2px;}
        .se-p{font-size:13.5px;color:#6B7280;line-height:1.55;margin:8px 0 20px;}
        .se-btn{width:100%;height:38px;border:none;border-radius:6px;background:#111;color:#fff;font-family:inherit;font-size:13.5px;font-weight:400;cursor:pointer;transition:background .15s,transform .15s;}
        .se-btn:hover{background:#2B2B2B;} .se-btn:active{transform:scale(.98);}
        .se-auto{margin-top:12px;font-size:12px;color:#9CA3AF;}
        .se-bar{height:2px;background:#F0F0F0;border-radius:1px;margin-top:8px;overflow:hidden;}
        .se-bar i{display:block;height:100%;background:#111;animation:se-time ${REDIRECT_SECONDS}s linear forwards;}
        @keyframes se-fade{from{opacity:0}to{opacity:1}}
        @keyframes se-up{from{opacity:0;transform:translateY(14px) scale(.98)}to{opacity:1;transform:none}}
        @keyframes se-draw{to{stroke-dashoffset:0}}
        @keyframes se-pop{from{transform:scale(0)}to{transform:scale(1)}}
        @keyframes se-bob{0%,100%{translate:0 0}45%{translate:0 -9px}60%{translate:0 2px}75%{translate:0 0}}
        @keyframes se-wink{0%,40%,100%{transform:rotate(0)}50%{transform:rotate(14deg)}62%{transform:rotate(0)}}
        @keyframes se-tilt{0%,100%{transform:rotate(0)}50%{transform:rotate(-4deg)}}
        @keyframes se-ripple{0%{opacity:.9;transform:scale(.85)}100%{opacity:0;transform:scale(1.35)}}
        @keyframes se-time{from{width:100%}to{width:0}}
        @media (prefers-reduced-motion:reduce){.se-ripple,.se-brow-g,.se-face{animation:none}.se-dot{animation:se-pop .01s forwards}}
      `}</style>
      <div className="se-card">
        <AnimatedSymbol/>
        <div className="se-t" id="se-t">Sesión caducada</div>
        <div className="se-p">Por seguridad, la sesión se cierra después de 2 horas. Talku sigue funcionando: solo vuelve a ingresar para continuar.</div>
        <button className="se-btn" onClick={goLogin} autoFocus>Ir al inicio de sesión</button>
        <div className="se-auto">Te llevamos automáticamente en {left} s</div>
        <div className="se-bar"><i/></div>
      </div>
    </div>,
    document.body
  );
}
